/**
 * Tenant resolution from the URL path (path-based multi-tenancy).
 *
 * URL convention: `bookdbeyond.com/{tenant_slug}/...`
 *   - First path segment IS the tenant slug.
 *   - Reserved segments (api, auth, oauth, static, assets, manage, book,
 *     admin, login, super-admin) are NEVER treated as a slug.
 *   - Empty path = visiting the SaaS root → caller decides what to do
 *     (e.g. redirect to the primary tenant or render a tenant directory).
 */

const RESERVED_SEGMENTS = new Set([
  'api', 'auth', 'oauth', 'static', 'assets',
  'robots.txt', 'sitemap.xml', 'favicon.ico', 'manifest.json',
  // App-level routes that pre-date the path-based pivot and should not be
  // mistaken for tenant slugs (also covered by tryLegacyRedirect below).
  'manage', 'book', 'admin', 'login', 'super-admin',
]);

const DEFAULT_TENANT_SLUG = 'mamabeauty';

export function getTenantSlugFromPath(pathname = window.location.pathname) {
  const seg = (pathname || '/').split('/').filter(Boolean)[0];
  if (!seg) return '';
  const lower = seg.toLowerCase();
  if (RESERVED_SEGMENTS.has(lower)) return '';
  if (!/^[a-z0-9-]+$/.test(lower)) return '';
  return lower;
}

export function getDefaultTenantSlug() {
  return DEFAULT_TENANT_SLUG;
}

export function tenantBasename(slug) {
  return slug ? `/${slug}` : '';
}

/**
 * Backwards-compat redirect for emails / bookmarks issued BEFORE the
 * path-based pivot. Returns the target path to redirect to, or null
 * if no rewrite applies.
 *
 *   /manage/abc        → /mamabeauty/manage/abc
 *   /book/mama-nails   → /mamabeauty/book
 *   /book              → /mamabeauty/book
 *   /book/mama-nails/x → /mamabeauty/book (extra segs stripped — booking
 *                                          wizard doesn't use sub-paths)
 *
 * NOTE: this only fires when the URL has NO tenant slug as first segment.
 * If a user is already at `/mamabeauty/manage/...`, no rewrite happens.
 */
export function getLegacyRedirectPath(pathname = window.location.pathname) {
  const fallback = DEFAULT_TENANT_SLUG;
  const manageMatch = pathname.match(/^\/manage\/(.+?)\/?$/);
  if (manageMatch) return `/${fallback}/manage/${manageMatch[1]}`;
  if (pathname === '/book' || /^\/book\/?$/.test(pathname)) return `/${fallback}/book`;
  const bookMatch = pathname.match(/^\/book\/[^/]+\/?.*$/);
  if (bookMatch) return `/${fallback}/book`;
  return null;
}
